import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, KeyRound, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@corebridge.io");
  const [password, setPassword] = useState("Apocalynx@2026");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to APOCALYNX MSPOS");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090B]">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center font-mono font-bold text-white text-lg">A</div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">APOCALYNX</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">MSPOS · Command Console</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Operator Sign-In</h1>
          <p className="text-sm text-zinc-500 mt-2 mb-6">Continue to the Compliance-Driven Managed Services Operating System.</p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Email</label>
              <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Password</label>
              <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading} className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "Continue"} <ArrowRight size={14} />
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <button data-testid="google-login" onClick={startGoogle} className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 border border-zinc-800 hover:border-zinc-700 text-zinc-200 text-sm font-medium rounded-md px-4 py-2 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#fff" opacity=".7" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/></svg>
            Continue with Google
          </button>

          <p className="text-xs text-zinc-500 mt-6">No account? <Link to="/register" className="text-blue-400 hover:underline" data-testid="register-link">Request access</Link></p>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative overflow-hidden border-l border-zinc-900">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#09090B] via-transparent to-[#09090B]" />
        <img src="https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxkYXJrJTIwc2VydmVyJTIwcm9vbXxlbnwwfHx8fDE3ODA4MDA3ODR8MA&ixlib=rb-4.1.0&q=85" alt="dark server room" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative h-full flex items-end p-12">
          <div className="max-w-md">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-2">Compliance-Driven Managed Services</div>
            <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight">
              One operating system for every endpoint, ticket, and signal.
            </h2>
            <p className="text-sm text-zinc-400 mt-4">RMM, PSA, Asset Lifecycle, Compliance, Security, Documentation, Credential Vault, Contracts, Licenses, Backups, Executive Intelligence — unified.</p>
            <div className="grid grid-cols-3 gap-2 mt-6">
              {[{i: ShieldCheck, l: "ISO·NIST·CIS·SEC·DPA·SOC2"}, {i: KeyRound, l: "Encrypted Vault"}, {i: Sparkles, l: "AI Operations"}].map((c, idx) => (
                <div key={idx} className="bg-zinc-950/70 border border-zinc-900 rounded-md p-3">
                  <c.i size={16} className="text-blue-400" />
                  <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest font-mono leading-snug">{c.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
